import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Sun, Droplet, Snowflake, PawPrint, ArrowLeft } from "lucide-react";
import { getCareGuide } from "../../data/careGuides";
import { PLANTS } from "../../data/plants";
import { SITE_IMAGES } from "../../data/siteImages";
import { EmptyState } from "../../components/fancyplants/states";
import ProductCard from "../../components/fancyplants/ProductCard";
import PlantImage from "../../components/fancyplants/PlantImage";
import Seo from "../../components/fancyplants/Seo";

export default function CareGuidePage() {
  const { slug } = useParams();
  const guide = getCareGuide(slug);

  useEffect(() => window.scrollTo(0, 0), [slug]);

  if (!guide) {
    return (
      <div className="container-fp pt-32 pb-20">
        <Seo title="Guide not found" noindex />
        <EmptyState
          title="Guide not found"
          message="We haven't written this one yet, or the link is mistyped."
          action={<Link to="/care" className="btn-forest">Back to the Care Library</Link>}
        />
      </div>
    );
  }

  const relatedPlant = PLANTS.find((p) => p.careGuideId === guide.id);

  // FAQPage schema for answer engines / rich results (GEO layer).
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: guide.faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="pt-24">
      <Seo
        title={`${guide.plantName} care`}
        description={guide.summary}
        path={`/care/${guide.slug}`}
        jsonLd={faqSchema}
      />

      <article className="container-fp py-10">
        <Link to="/care" className="inline-flex items-center gap-1.5 text-sm text-ink-soft hover:text-forest-700">
          <ArrowLeft size={16} /> Care Library
        </Link>

        <div className="mx-auto mt-6 max-w-3xl">
          <p className="eyebrow mb-2">Care guide</p>
          <h1 className="t-h1">{guide.plantName}</h1>
          <p className="t-lead mt-4">{guide.summary}</p>

          <PlantImage
            src={relatedPlant?.images[0] || SITE_IMAGES.plantShelf}
            alt={relatedPlant ? `${relatedPlant.name} foliage` : `${guide.plantName} foliage`}
            ratio="16/9"
            className="mt-8 rounded-3xl"
            priority
          />

          <div className="mx-auto my-8 grid max-w-prose grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat icon={Sun} label="Light" value={guide.stats.light} />
            <Stat icon={Droplet} label="Water" value={guide.stats.water} />
            <Stat icon={Snowflake} label="Hardiness" value={guide.stats.hardiness} />
            <Stat icon={PawPrint} label="Toxicity" value={guide.stats.toxicity} />
          </div>

          <div className="mx-auto max-w-prose space-y-7">
            {guide.faq.map((item) => (
              <div key={item.q} className="border-b border-line pb-7">
                <h2 className="font-display text-2xl leading-snug">{item.q}</h2>
                <p className="mt-3 text-lg leading-relaxed text-ink-soft">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </article>

      {relatedPlant && (
        <section className="bg-canvas-raised">
          <div className="container-fp py-14">
            <div className="grid items-center gap-8 md:grid-cols-[1fr_320px]">
              <div>
                <p className="eyebrow mb-2">In stock</p>
                <h2 className="t-h2 mb-3">Bring home a {relatedPlant.name}</h2>
                <p className="t-lead">
                  Everything in this guide applies to the {relatedPlant.name} we grow and pack
                  ourselves — chosen for exactly these conditions.
                </p>
              </div>
              <div className="max-w-xs">
                <ProductCard plant={relatedPlant} />
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="surface p-3">
      <Icon size={18} className="mb-1.5 text-forest-500" />
      <div className="text-[11px] uppercase tracking-wide text-ink-soft">{label}</div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  );
}
